import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFieldAuth } from '../../contexts/FieldAuthContext';
import { authApi } from '../../services/api';
import './FieldLoginPage.css';

const FieldLoginPage: React.FC = () => {
    const [phoneOrEmail, setPhoneOrEmail] = useState(() => localStorage.getItem('last_specialist_phone') || '');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [testingConnection, setTestingConnection] = useState(false);

    // 4 Haneli PIN Belirleme State'leri
    const [showPinSetup, setShowPinSetup] = useState(false);
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [changePinLoading, setChangePinLoading] = useState(false);
    const [changePinError, setChangePinError] = useState('');

    const { isAuthenticated, user, login, changePin, forcePasswordChange, clearForcePasswordChange } = useFieldAuth();
    const navigate = useNavigate();

    // 3 Saniyelik Giriş Yükleme / Karşılama Ekranı (Sadece Giriş Yapıldığında çalışır, Çıkışta ASLA çalışmaz)
    const [showSplash, setShowSplash] = useState(false);
    const [splashFading, setSplashFading] = useState(false);


    // Giriş yapılmış ve PIN formu açık değilse doğrudan Saha Paneli'ne yönlendir
    useEffect(() => {
        if (isAuthenticated && user && !showPinSetup && !forcePasswordChange?.required) {
            navigate('/field/dashboard', { replace: true });
        }
    }, [isAuthenticated, user, showPinSetup, forcePasswordChange, navigate]);

    // 4 Haneli PIN Kaydetme
    const handleChangePinSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setChangePinError('');

        if (newPin.length !== 4) {
            setChangePinError('PIN tam olarak 4 haneli olmalıdır');
            return;
        }

        if (newPin !== confirmPin) {
            setChangePinError('Girdiğiniz PIN kodları birbiriyle eşleşmiyor');
            return;
        }

        // Kural 1: Ardışık olamaz (1234, 4321)
        if (newPin === '1234' || newPin === '4321') {
            setChangePinError('Güvenlik nedeniyle PIN ardışık (1234, 4321) olamaz');
            return;
        }

        // Kural 2: Aynı rakamlardan oluşamaz (1111, 2222 vb.)
        if (/^(\d)\1{3}$/.test(newPin)) {
            setChangePinError('PIN aynı rakamlardan (1111 vb.) oluşamaz');
            return;
        }

        // Kural 3: Doğum yılı belirtemez (1900-2100)
        const year = parseInt(newPin, 10);
        if (year >= 1900 && year <= 2100) {
            setChangePinError('PIN bir yıl (doğum yılı vb.) olamaz');
            return;
        }

        try {
            setChangePinLoading(true);
            await changePin(newPin);
            alert('🎉 4 haneli hızlı mobil PIN kodunuz başarıyla kaydedildi! Artık sahada tek dokunuşla bu PIN ile giriş yapabilirsiniz.');
            clearForcePasswordChange();
            setShowPinSetup(false);
            navigate('/field/dashboard', { replace: true });
        } catch (err: any) {
            setChangePinError(err.message || 'PIN kaydı başarısız');
        } finally {
            setChangePinLoading(false);
        }
    };

    // Mobil Giriş İşlemi
    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const identifier = phoneOrEmail.trim();
        if (!identifier) {
            setError('Lütfen e-posta adresinizi veya cep telefonu numaranızı giriniz');
            return;
        }

        if (!password.trim()) {
            setError('Lütfen şifrenizi veya 4 haneli PIN kodunuzu giriniz');
            return;
        }

        try {
            setIsLoading(true);
            setShowSplash(true);
            setSplashFading(false);
            localStorage.setItem('last_specialist_phone', identifier);

            // Arka planda login isteğini ve minimum 4.5 saniyelik şık açılış animasyonunu paralel çalıştır (reklam & kurumsal kimlik gösterimi)
            const [loggedInUser] = await Promise.all([
                login(identifier, password),
                new Promise((resolve) => setTimeout(resolve, 4500))
            ]);

            setSplashFading(true);
            await new Promise((resolve) => setTimeout(resolve, 400));

            // Eğer kullanıcının henüz 4 haneli mobil PIN'i yoksa, isteğe bağlı hızlı PIN teklif et
            if (!loggedInUser.hasMobilePin) {
                setShowSplash(false);
                setShowPinSetup(true);
            } else {
                navigate('/field/dashboard', { replace: true });
            }
        } catch (err: any) {
            setShowSplash(false);
            setSplashFading(false);
            if (err.message === 'MUST_CHANGE_PASSWORD') {
                // UI otomatik olarak forcePasswordChange formunu render eder
            } else {
                setError(err.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol ediniz.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Bağlantı Testi (Ping)
    const testConnection = async () => {
        setTestingConnection(true);
        setError('');

        try {
            const start = Date.now();
            const res = await authApi.ping();
            const duration = Date.now() - start;

            if (res && res.success) {
                alert(`Bağlantı Başarılı\n\nUzman Destek sunucusuna erişilebiliyor.\nSüre: ${duration}ms`);
            } else {
                throw new Error('Sunucu yanıt vermedi');
            }
        } catch (err: any) {
            setError(`Bağlantı Hatası: ${err.message}`);
        } finally {
            setTestingConnection(false);
        }
    };

    // 4 Haneli PIN Belirleme Ekranı (İlk Giriş veya İsteğe Bağlı Hızlı PIN)
    if (forcePasswordChange?.required || showPinSetup) {
        return (
            <div className="field-login-container">
                <div className="field-login-content">
                    <div className="field-login-header">
                        <img 
                            src="/icons/icon-192.png" 
                            alt="Uzman Destek" 
                            style={{ 
                                width: 64, 
                                height: 64, 
                                borderRadius: 16, 
                                boxShadow: '0 8px 20px rgba(30, 60, 114, 0.2)', 
                                marginBottom: 10,
                                display: 'inline-block'
                            }} 
                        />
                        <h1 className="field-login-title">Hızlı Mobil PIN Belirleyin</h1>
                        <p className="field-login-subtitle">
                            Sahada her defasında uzun şifre girmemek için 4 haneli kolay bir PIN belirleyin
                        </p>
                    </div>

                    <form className="field-login-form" onSubmit={handleChangePinSubmit}>
                        {changePinError && (
                            <div className="field-error-message">
                                {changePinError}
                            </div>
                        )}

                        <div className="field-input-group">
                            <label className="field-label">Yeni 4 Haneli PIN</label>
                            <input
                                type="password"
                                className="field-input"
                                placeholder="••••"
                                value={newPin}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 4) setNewPin(val);
                                }}
                                disabled={changePinLoading}
                                maxLength={4}
                                inputMode="numeric"
                                autoFocus
                            />
                        </div>

                        <div className="field-input-group">
                            <label className="field-label">PIN Tekrar</label>
                            <input
                                type="password"
                                className="field-input"
                                placeholder="••••"
                                value={confirmPin}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    if (val.length <= 4) setConfirmPin(val);
                                }}
                                disabled={changePinLoading}
                                maxLength={4}
                                inputMode="numeric"
                            />
                        </div>

                        <ul className="field-pin-rules">
                            <li>Tam 4 rakamdan oluşmalıdır.</li>
                            <li>Ardışık olamaz (1234, 4321).</li>
                            <li>Aynı rakamlardan oluşamaz (1111 vb.).</li>
                            <li>Yıl belirtemez (1990 vb.).</li>
                        </ul>

                        <button
                            type="submit"
                            className="field-button field-button-primary"
                            disabled={changePinLoading}
                        >
                            {changePinLoading ? (
                                <>
                                    <span className="field-spinner"></span>
                                    Kaydediliyor...
                                </>
                            ) : (
                                'PIN Kodunu Kaydet ve Devam Et'
                            )}
                        </button>

                        {/* Eğer zorunlu değilse (isteğe bağlı teklif edildiyse), geçme butonu */}
                        {!forcePasswordChange?.required && (
                            <button
                                type="button"
                                className="field-button field-button-outline"
                                onClick={() => {
                                    setShowPinSetup(false);
                                    navigate('/field/dashboard', { replace: true });
                                }}
                                disabled={changePinLoading}
                            >
                                Şimdi Değil, Doğrudan Devam Et 👉
                            </button>
                        )}
                    </form>
                </div>
            </div>
        );
    }

    // Standart Mobil Giriş Formu (Seçenek 1 & 3 Entegrasyonu)
    return (
        <div className="field-login-container">
            {showSplash && (
                <div 
                    className={`field-splash-screen ${splashFading ? 'field-splash-fade-out' : ''}`}
                >
                    <div className="field-splash-content">
                        <div className="field-splash-logo-wrapper">
                            <img 
                                src="/icons/icon-192.png" 
                                alt="Uzman Destek" 
                                className="field-splash-logo" 
                            />
                        </div>
                        <h1 className="field-splash-title">Uzman Destek</h1>
                        <p className="field-splash-motto">Sahadaki Yardımcınız</p>
                        <div className="field-splash-divider"></div>
                        <div className="field-splash-web">www.isgdestek.com.tr</div>

                        <div className="field-splash-progress-track">
                            <div className="field-splash-progress-bar"></div>
                        </div>

                        <span className="field-splash-skip">Geçmek için dokunun</span>
                    </div>
                </div>
            )}

            <div className="field-login-content">
                <div className="field-login-header">
                    <img 
                        src="/icons/icon-192.png" 
                        alt="Uzman Destek" 
                        style={{ 
                            width: 72, 
                            height: 72, 
                            borderRadius: 18, 
                            boxShadow: '0 8px 24px rgba(30, 60, 114, 0.25)', 
                            marginBottom: 12,
                            display: 'inline-block'
                        }} 
                    />
                    <h1 className="field-login-title">Uzman Destek Mobil</h1>
                    <p className="field-login-subtitle">İSG Uzmanı Sahada Hızlı Giriş</p>
                </div>

                <form className="field-login-form" onSubmit={handleLogin}>
                    {error && (
                        <div className="field-error-message">
                            {error}
                        </div>
                    )}

                    <div className="field-input-group">
                        <label className="field-label">E-Posta Adresi veya Cep Telefonu</label>
                        <input
                            type="text"
                            className="field-input"
                            placeholder="ornek@uzmanisg.com veya 5xxxxxxxxx"
                            value={phoneOrEmail}
                            onChange={(e) => setPhoneOrEmail(e.target.value)}
                            disabled={isLoading}
                            autoComplete="username"
                        />
                    </div>

                    <div className="field-input-group">
                        <div className="field-label-row">
                            <label className="field-label">Şifre veya 4 Haneli PIN</label>
                            <Link 
                                to="/forgot-password" 
                                className="field-forgot-link"
                                tabIndex={-1}
                            >
                                Şifremi Unuttum?
                            </Link>
                        </div>
                        <input
                            type="password"
                            className="field-input"
                            placeholder="Web şifreniz veya 4 haneli PIN"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isLoading}
                            autoComplete="current-password"
                        />
                        <span className="field-input-hint">
                            💡 Web şifrenizle veya daha önce belirlediğiniz 4 haneli hızlı mobil PIN'inizle giriş yapabilirsiniz.
                        </span>
                    </div>

                    <button
                        type="submit"
                        className="field-button field-button-primary"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="field-spinner"></span>
                                Giriş yapılıyor...
                            </>
                        ) : (
                            'Giriş Yap'
                        )}
                    </button>

                    <button
                        type="button"
                        className="field-button field-button-secondary"
                        onClick={testConnection}
                        disabled={isLoading || testingConnection}
                    >
                        {testingConnection ? 'Test ediliyor...' : 'Bağlantı Testi'}
                    </button>
                </form>

                <div className="field-login-footer">
                    <Link to="/login" className="field-web-switch-link">
                        🖥️ Masaüstü Web Girişine Git
                    </Link>
                    <p className="field-version">v1.0 Mobil PWA • Uçtan Uca Şifreli</p>
                </div>
            </div>
        </div>
    );
};

export default FieldLoginPage;
